// Barge-in and the half-duplex echo gate (BUILD 2 C2-8, WP2-8 steps 8 and 9).
//
// Acoustic and structural only: there is never a comparison between what the
// microphone heard and the reply being spoken.
//
// - A tap while a reply plays stops it at once
//   (`player.stop('barge_in')`, ≤ 150 ms) — the controller calls `interrupt`.
// - Every hands-free session detects interruption locally: three 20 ms input
//   windows must have residual energy above the noise floor and exceed the
//   coherent playback estimate. ASR text
//   never confirms an onset, since it may be delayed or be speaker echo.
// - Audio stays local until interruption is confirmed, even on a Gate B
//   platform: remote ASR echo must not contaminate the next turn. Gate B allows
//   skipping the passive echo tail after normal playback finishes.
// - Half duplex (the default): frames are not sent while a reply plays, and
//   not until `outputLatency + baseLatency + 300 ms` after it stops, because
//   Bluetooth output can lag by more than 200 ms. The reply's own tail
//   therefore cannot open a hands-free turn.
import { createAcousticOnset } from './acoustic-onset';
import type { PlaybackStop, SpeechPlayer } from './contracts';
import { ECHO_TAIL_MS } from './policy';
import type { VoiceTimers } from './session';

type Latency = { outputLatency?: number; baseLatency?: number };

/** The echo tail after playback stops, in ms: output and base latency plus 300 ms. */
export function echoTailMs(context: Latency | null): number {
  const seconds = (context?.outputLatency ?? 0) + (context?.baseLatency ?? 0);
  return Math.max(0, Math.round(seconds * 1000)) + ECHO_TAIL_MS;
}

export type BargeInDeps = {
  player?: SpeechPlayer;
  timers: VoiceTimers;
  now(): number;
  /** The shared AudioContext's latencies, read when playback stops. */
  latency(): Latency | null;
  /** The normal voice-activity threshold (the policy's). */
  voiceActivityLevel: number;
  /** The reply stopped because the person spoke over it. */
  onBargeIn(stop: PlaybackStop | null, onsetAt: number): void;
};

export type BargeIn = {
  /** Whether a captured frame may be sent now (half duplex gates the reply and its tail). */
  micOpen(): boolean;
  /** A tap: stop the reply now. */
  interrupt(): PlaybackStop | null;
  /** One 20 ms window's microphone RMS, after browser echo cancellation. */
  frame(rms: number, reference?: number, at?: number): void;
  setFullDuplex(on: boolean): void;
  dispose(): void;
};

export function createBargeIn(deps: BargeInDeps): BargeIn {
  let fullDuplex = false;
  let gatedUntil = 0;
  let wasPlaying = false;
  const onset = createAcousticOnset(deps.voiceActivityLevel, deps.now);
  let capturedInterruption = false;
  let disposed = false;

  const playing = () => {
    const state = deps.player?.state();
    return state === 'speaking' || state === 'loading';
  };

  // Track the end of playback, for the echo tail.
  const unsubscribe = deps.player?.subscribe(() => {
    const now = playing();
    if (!wasPlaying && now) capturedInterruption = false;
    if (wasPlaying && !now) gatedUntil = deps.now() + echoTailMs(deps.latency());
    if (!now) onset.reset();
    wasPlaying = now;
  });

  const interrupt = (): PlaybackStop | null => {
    if (disposed) return null;
    onset.reset();
    if (!playing()) return null;
    const stop = deps.player?.stop('barge_in') ?? null;
    wasPlaying = false;
    gatedUntil = deps.now() + echoTailMs(deps.latency());
    return stop;
  };

  return {
    micOpen: () => {
      if (disposed) return false;
      if (playing()) return false;
      if (fullDuplex || capturedInterruption) return true;
      return deps.now() >= gatedUntil;
    },
    interrupt,
    frame(rms, reference = deps.player?.playbackLevel?.(), at = deps.now()) {
      if (disposed) return;
      if (!playing()) return;
      const activity = onset.push(rms, reference, at);
      if (!activity?.confirmed) return;
      const stop = interrupt();
      // Preserve the new speaker's first word instead of gating it as echo.
      capturedInterruption = true;
      deps.onBargeIn(stop, activity.startedAt);
    },
    setFullDuplex(on) {
      if (disposed) return;
      fullDuplex = on;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      onset.reset();
      unsubscribe?.();
    },
  };
}
